import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Dialog, ErrorAlert, Loading, TextField } from '../components/kit.tsx';
import { SafeLink } from '../components/SafeLink.tsx';
import { command, query } from '../lib/api.ts';
import { usePageTitle } from '../lib/format.ts';

export type CohortRow = {
  cohortId: string; code: string; labelTh: string; labelEn: string | null; state: 'planning' | 'active' | 'archived';
  academicYearBe: number; activePolicyVersion: number | null; phase: string; enrolled: number; projects: number; unplaced: number;
};

/**
 * Cohorts (roadmap 4.1). A cohort starts in planning (sign-ins with its prefix wait for review), is
 * activated when its roster is in, and is archived once no group is forming or matched.
 */
export function AdminCohorts() {
  const { t, i18n } = useTranslation();
  usePageTitle(t('adminCohorts.title'));
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['cohort.list'], queryFn: () => query<CohortRow[]>('cohort.list') });
  const [confirm, setConfirm] = useState<{ cohort: CohortRow; state: 'active' | 'archived' } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const setState = useMutation({
    mutationFn: (v: { cohortId: string; state: 'active' | 'archived' }) => command('cohort.setState', v),
    onSuccess: async () => {
      setConfirm(null);
      setNotice(t('adminCohorts.stateChanged'));
      await qc.invalidateQueries({ queryKey: ['cohort.list'] });
    },
  });
  const label = (c: CohortRow) => (i18n.language !== 'th' && c.labelEn ? c.labelEn : c.labelTh);
  return (
    <div className="stack">
      <h1>{t('adminCohorts.title')}</h1>
      <p>{t('adminCohorts.intro')}</p>
      {notice ? <Alert tone="success"><p>{notice}</p></Alert> : null}
      <ErrorAlert error={list.error} />
      {list.isPending ? <Loading /> : null}
      {list.data ? (
        <div className="table-wrap">
          <table className="table">
            <caption className="visually-hidden">{t('adminCohorts.title')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('adminCohorts.code')}</th>
                <th scope="col">{t('adminCohorts.label')}</th>
                <th scope="col">{t('adminCohorts.state')}</th>
                <th scope="col">{t('adminCohorts.phase')}</th>
                <th scope="col" className="num">{t('adminCohorts.enrolled')}</th>
                <th scope="col" className="num">{t('adminCohorts.projects')}</th>
                <th scope="col" className="num">{t('adminCohorts.unplaced')}</th>
                <th scope="col">{t('adminCohorts.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((c) => (
                <tr key={c.cohortId} data-testid="cohort-row">
                  <th scope="row"><SafeLink href={`/admin/cohort?id=${c.cohortId}`}>{c.code}</SafeLink></th>
                  <td>{label(c)} <span className="muted small">({t('adminCohorts.year', { year: c.academicYearBe })})</span></td>
                  <td><span className="tag">{t(`adminCohorts.states.${c.state}`)}</span></td>
                  <td>{t(`adminCohorts.phases.${c.phase}`)}</td>
                  <td className="num">{c.enrolled}</td>
                  <td className="num">{c.projects}</td>
                  <td className="num">{c.unplaced}</td>
                  <td>
                    {c.state === 'planning' ? (
                      <Button variant="secondary" onClick={() => setConfirm({ cohort: c, state: 'active' })}
                        aria-label={t('adminCohorts.activateLabel', { code: c.code })}>{t('adminCohorts.activate')}</Button>
                    ) : null}
                    {c.state === 'active' ? (
                      <Button variant="secondary" onClick={() => setConfirm({ cohort: c, state: 'archived' })}
                        aria-label={t('adminCohorts.archiveLabel', { code: c.code })}>{t('adminCohorts.archive')}</Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <CreateCohort onCreated={async () => {
        setNotice(t('adminCohorts.created'));
        await qc.invalidateQueries({ queryKey: ['cohort.list'] });
      }} />
      <Dialog open={confirm !== null} title={confirm?.state === 'active' ? t('adminCohorts.activate') : t('adminCohorts.archive')}
        onCancel={() => setConfirm(null)} actions={(
          <>
            <Button busy={setState.isPending} onClick={() => confirm && setState.mutate({ cohortId: confirm.cohort.cohortId, state: confirm.state })}>
              {t('adminCohorts.confirm')}
            </Button>
            <Button variant="secondary" onClick={() => setConfirm(null)}>{t('common.cancel')}</Button>
          </>
        )}>
        <p>{confirm?.state === 'active' ? t('adminCohorts.activateBody', { code: confirm.cohort.code }) : t('adminCohorts.archiveBody', { code: confirm?.cohort.code ?? '' })}</p>
        <ErrorAlert error={setState.error} />
      </Dialog>
    </div>
  );
}

function CreateCohort({ onCreated }: { onCreated(): Promise<void> }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ code: '', year: '', labelTh: '', labelEn: '' });
  const [errors, setErrors] = useState<Partial<Record<keyof typeof form, string>>>({});
  const create = useMutation({
    mutationFn: () => command('cohort.create', {
      code: form.code.trim(), academicYearBe: Number(form.year), labelTh: form.labelTh.trim(),
      ...(form.labelEn.trim() ? { labelEn: form.labelEn.trim() } : {}),
    }),
    onSuccess: async () => {
      setForm({ code: '', year: '', labelTh: '', labelEn: '' });
      await onCreated();
    },
  });
  const set = (k: keyof typeof form) => (e: { currentTarget: HTMLInputElement }) => {
    const v = e.currentTarget.value;
    setForm((f) => ({ ...f, [k]: v }));
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!/^[0-9]{2}$/.test(form.code.trim())) next.code = t('adminCohorts.codeInvalid');
    const y = Number(form.year);
    if (!Number.isInteger(y) || y < 2500 || y > 2700) next.year = t('adminCohorts.yearInvalid');
    if (!form.labelTh.trim()) next.labelTh = t('adminCohorts.labelRequired');
    setErrors(next);
    if (Object.keys(next).length === 0) create.mutate();
  };
  return (
    <section className="card" aria-labelledby="cohort-create">
      <h2 id="cohort-create">{t('adminCohorts.createTitle')}</h2>
      <form onSubmit={submit} noValidate>
        <TextField label={t('adminCohorts.code')} hint={t('adminCohorts.codeHint')} value={form.code} inputMode="numeric" maxLength={2}
          required error={errors.code ?? null} onChange={set('code')} />
        <TextField label={t('adminCohorts.yearLabel')} value={form.year} inputMode="numeric" maxLength={4} required
          error={errors.year ?? null} onChange={set('year')} />
        <TextField label={t('adminCohorts.labelTh')} value={form.labelTh} required error={errors.labelTh ?? null} lang="th" onChange={set('labelTh')} />
        <TextField label={t('adminCohorts.labelEn')} value={form.labelEn} lang="en" onChange={set('labelEn')} />
        <ErrorAlert error={create.error} />
        <Button type="submit" busy={create.isPending}>{t('adminCohorts.create')}</Button>
      </form>
    </section>
  );
}
